import { useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AuthSplitLayout } from '../components/AuthSplitLayout.tsx';
import { Btn } from '../components/Btn.tsx';
import { CodeInput } from './CodeInput.tsx';
import {
  missingChallengeMessage,
  resendCooldownRemaining,
  sendSmsChallenge,
  stepUp,
  verifyPhone,
  type FactorType,
} from '../lib/mfa.ts';

// Prove the enrolled factor so this session reaches aal2, which is what
// companyAuth requires of every /api/me call (spec D6).
//
// Like the enrolment screen, this talks ONLY to Supabase — no Worker call
// happens until the session is already aal2.
export function MfaStepUpScreen({
  client,
  factorId,
  factorType,
  supportEmail,
  onVerified,
  onSignOut,
}: {
  client: SupabaseClient;
  factorId: string;
  factorType: FactorType;
  supportEmail: string;
  onVerified: () => void;
  onSignOut: () => void;
}) {
  const isPhone = factorType === 'phone';
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const submitting = useRef(false);

  const cooldown = resendCooldownRemaining(sentAt, now);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // NOTHING IS SENT ON MOUNT. The vendor presses "Text me a code" (send()
  // below), and only then does a text go out.
  //
  // This screen is reached through the emailed login link, and the link is
  // opened by more than the vendor. The fedex.com mail filter opens it from
  // Microsoft IPs, runs the SPA in a headless browser that reports itself as
  // visible, and lands here holding a real session. While this screen sent on
  // mount, every scan texted the vendor a code, and the filter rescans: five
  // texts for one link on Test, 27-Oct, three of them inside two minutes. The
  // Worker's SMS cooldown and cap do not help, because they guard only the
  // code sign-in route; this screen talks to GoTrue directly. Scanners open
  // links but do not press buttons.
  //
  // Sending on click also retires the two mount-time faults the old effect
  // had to fence off: StrictMode's double-invoked effect sending twice (4-Sep),
  // and every open tab racing for GoTrue's rate limit when supabase-js
  // broadcasts a sign-in (15-Oct, PR #101).
  async function send() {
    if (sending || busy || cooldown > 0) return;
    setErr(null);
    setSending(true);
    try {
      setChallengeId(await sendSmsChallenge(client, factorId));
      setCode('');
      setSentAt(Date.now());
      setNow(Date.now());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function submit(value: string) {
    if (value.length !== 6 || submitting.current) return;
    if (isPhone && !challengeId) {
      // Never silent: a code typed here has nothing to be checked against.
      setErr(missingChallengeMessage(sending));
      return;
    }
    submitting.current = true;
    setErr(null);
    setBusy(true);
    try {
      if (isPhone) await verifyPhone(client, factorId, challengeId as string, value);
      else await stepUp(client, factorId, value);
      setCode('');
      onVerified();
    } catch (e) {
      setErr((e as Error).message);
      // Clear the rejected digits — the field is already at maxLength, so
      // otherwise the vendor must backspace six times to try again.
      setCode('');
    } finally {
      setBusy(false);
      submitting.current = false;
    }
  }

  function onCode(next: string) {
    setCode(next);
    if (next.length < 6) setErr(null);
    if (next.length === 6) void submit(next); // auto-verify; the button is the fallback
  }

  // A phone factor with no challenge yet has no code to check, so it shows only
  // the send button. A failed first send stays here, with the error, to retry.
  const awaitingSend = isPhone && !challengeId;

  return (
    <AuthSplitLayout
      variant="vendor"
      headline="One more step"
      subtext="Confirm it is really you before we open your account."
    >
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl text-slate-900">
          {awaitingSend ? 'Get your code' : 'Enter your code'}
        </h2>
        <p className="text-sm leading-relaxed text-slate-500">
          {awaitingSend
            ? 'We will text a 6-digit code to the mobile number on your account.'
            : isPhone
              ? 'Enter the 6-digit code we just texted you.'
              : 'Enter the current 6-digit code from your authenticator app.'}
        </p>

        {!awaitingSend && <CodeInput value={code} onChange={onCode} disabled={busy} autoFocus />}

        {err && (
          // role="alert" so a screen reader announces a rejected code; the
          // auto-verify means there is no submit event to hear otherwise.
          <p role="alert" className="text-sm text-red-600">
            {err}
          </p>
        )}

        {awaitingSend ? (
          <Btn onClick={() => void send()} loading={sending} className="w-full">
            Text me a code
          </Btn>
        ) : (
          <Btn
            onClick={() => void submit(code)}
            loading={busy}
            disabled={code.length < 6}
            className="w-full"
          >
            Verify and continue
          </Btn>
        )}

        {isPhone && !awaitingSend && (
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending || busy || cooldown > 0}
            className="self-start text-sm font-semibold text-brand-primary disabled:opacity-50"
          >
            {sending ? 'Sending…' : cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
          </button>
        )}

        <div className="pt-1 text-sm text-slate-500">
          {isPhone ? 'Changed your number? Contact ' : 'Lost your authenticator? Contact '}
          <a href={`mailto:${supportEmail}`} className="font-semibold text-brand-primary">
            {supportEmail}
          </a>{' '}
          — they can reset your two-factor setup.
        </div>
        <button
          type="button"
          onClick={onSignOut}
          className="self-start text-sm font-semibold text-slate-500 hover:underline"
        >
          Sign out
        </button>
      </div>
    </AuthSplitLayout>
  );
}
